import React, { useState } from "react";

/**
 * DataTableCard
 * ──────────────
 * Paginated, sortable data table — blue/white brand theme.
 * Rendered when structured_data.structured_data_type === "data_table".
 *
 * Props
 * -----
 * data : data_table payload from format_data_table tool
 */

/**
 * Convert raw dataset column names into readable labels:
 *   age_group        → Age Group
 *   total_spent_usd  → Total Spent USD
 *   firstName        → First Name
 *   createdAt        → Created At
 *   amount_usd       → Amount USD
 */
const KNOWN_UPPER = new Set(["id", "usd", "url", "api", "sku", "crm", "erp", "mrr", "arr"]);

const toTitleCase = (str) =>
  String(str)
    // camelCase → spaces: "totalSpentUsd" → "total Spent Usd"
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    // underscores/hyphens → spaces
    .replace(/[_-]+/g, " ")
    // collapse extra spaces
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      const lower = word.toLowerCase();
      // Known acronyms stay fully uppercase
      if (KNOWN_UPPER.has(lower)) return lower.toUpperCase();
      // Everything else: capitalise first letter
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");

const DataTableCard = ({ data }) => {
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [search, setSearch] = useState("");

  if (!data) return null;

  const {
    title = "",
    columns = [],
    rows = [],
    sources = [],
  } = data;
  
  const page_size = 5;

  // Search filter
  const filtered = search.trim()
    ? rows.filter((row) =>
        columns.some((col) =>
          String(row[col] ?? "").toLowerCase().includes(search.toLowerCase())
        )
      )
    : rows;

  // Sort
  const sorted = sortCol
    ? [...filtered].sort((a, b) => {
        const av = a[sortCol];
        const bv = b[sortCol];
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        if (typeof av === "number" && typeof bv === "number")
          return sortAsc ? av - bv : bv - av;
        return sortAsc
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      })
    : filtered;

  // Pagination
  const totalPages = Math.ceil(sorted.length / page_size);
  const currentPage = Math.min(page, Math.max(0, totalPages - 1));
  const pageRows = sorted.slice(
    currentPage * page_size,
    (currentPage + 1) * page_size
  );

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc((v) => !v);
    else { setSortCol(col); setSortAsc(true); }
    setPage(0);
  };

  const formatCell = (v) => {
    if (v === null || v === undefined)
      return <span className="text-gray-300">—</span>;
    if (typeof v === "number") return v.toLocaleString();
    return String(v);
  };

  return (
    /*
     * Width: w-fit so the table is only as wide as its content when data is
     * small. max-w-full prevents it from overflowing its parent on large data.
     * min-w-[280px] keeps very narrow results readable.
     */
    <div className="w-fit max-w-full min-w-[280px] rounded-2xl border border-blue-200 bg-white shadow-md overflow-hidden">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="px-4 py-3 bg-blue-600 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {/* table icon */}
          <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M3 10h18M3 14h18M3 6h18M3 18h18" />
          </svg>
          <span className="text-[1.05rem] font-black text-white tracking-widest drop-shadow-md">
            {title || "Results"}
          </span>
          <span className="text-[0.72rem] font-semibold text-blue-100 bg-blue-500/50 px-2 py-0.5 rounded-full">
            {sorted.length.toLocaleString()} rows
          </span>
        </div>

        {/* Search */}
        <div className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-lg px-2.5 py-1 text-[0.8rem]">
          <svg className="w-3.5 h-3.5 text-white/60 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Filter..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="outline-none bg-transparent w-24 text-white placeholder-white/50 font-medium text-[0.8rem]"
          />
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto">
        {/* table-auto: columns size to their content, not stretched to full width */}
        <table className="table-auto text-[0.82rem] border-collapse">
          <thead>
            {/* Solid blue header row — white text, visible column separators */}
            <tr className="bg-blue-600">
              {columns.map((col) => (
                <th
                  key={col}
                  onClick={() => handleSort(col)}
                  className="px-4 py-3 text-left text-[0.75rem] font-bold text-white bg-blue-600 cursor-pointer hover:bg-blue-700 transition whitespace-nowrap select-none border-r border-r-blue-500 last:border-r-0"
                >
                  <span className="flex items-center gap-1">
                    {toTitleCase(col)}
                    {sortCol === col ? (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        {sortAsc
                          ? <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          : <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        }
                      </svg>
                    ) : (
                      <svg className="w-3 h-3 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L5 6.414V12zm10-4a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z" />
                      </svg>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-6 text-center text-gray-400 text-sm italic"
                >
                  {search ? "No rows match the filter." : "No data."}
                </td>
              </tr>
            ) : (
              pageRows.map((row, i) => (
                <tr
                  key={i}
                  className="group border-b border-blue-100 last:border-b-0"
                >
                  {columns.map((col) => (
                    <td
                      key={col}
                      className="px-4 py-2.5 text-black font-semibold bg-white group-hover:bg-blue-50/60 transition-colors whitespace-nowrap border-r border-blue-100 last:border-r-0"
                    >
                      {formatCell(row[col])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Footer: pagination + sources ────────────────────────────────────── */}
      <div className="px-4 py-2.5 flex items-center justify-center gap-4 bg-white border-t border-blue-100">

        {/* Pagination controls */}
        <div className="flex items-center gap-1.5">
          {/* Left arrow */}
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="px-3 py-1 flex items-center justify-center rounded-lg border border-blue-200 text-[0.8rem] text-blue-600 font-bold disabled:opacity-40 hover:bg-blue-100 active:scale-95 transition"
            aria-label="Previous page"
          >
            Prev
          </button>

          {/* Page indicator */}
          <span className="text-[0.72rem] text-blue-500 font-semibold min-w-[80px] text-center">
            {sorted.length === 0
              ? "0 rows"
              : `${currentPage * page_size + 1}–${Math.min((currentPage + 1) * page_size, sorted.length)} of ${sorted.length}`
            }
          </span>

          {/* Right arrow */}
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="px-3 py-1 flex items-center justify-center rounded-lg border border-blue-200 text-[0.8rem] text-blue-600 font-bold disabled:opacity-40 hover:bg-blue-100 active:scale-95 transition"
            aria-label="Next page"
          >
            Next
          </button>
        </div>

        {sources && sources.length > 0 && (
          <div className="flex gap-1.5">
            {sources.map((s, i) => (
              <span
                key={i}
                className="text-[0.65rem] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full uppercase tracking-wide"
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTableCard;
